import React from 'react';
import { cn } from '@/lib/utils';

// Rating enum values must match database enums
export type RatingValue = 'steal' | 'fair_deal' | 'splurge' | 'daylight_robbery';
export type RatingPortion = 'snack_attack' | 'just_right' | 'food_coma';
export type RatingTaste = 'meh' | 'solid' | 'fire' | 'life_changing';

export interface ReviewRatings {
  ratingValue: RatingValue | null;
  ratingPortion: RatingPortion | null;
  ratingTaste: RatingTaste | null;
}

interface ReviewMatrixProps {
  value: ReviewRatings;
  onChange: (key: keyof ReviewRatings, value: string) => void;
}

interface RatingOption {
  id: string;
  label: string;
  emoji: string;
  subtitle: string;
}

const VALUE_OPTIONS: RatingOption[] = [
  { id: 'steal', label: 'Steal!', emoji: '🤑', subtitle: 'Amazing value' },
  { id: 'fair_deal', label: 'Fair Deal', emoji: '✅', subtitle: 'Worth it' },
  { id: 'splurge', label: 'Splurge', emoji: '💎', subtitle: 'Expensive but good' },
  { id: 'daylight_robbery', label: 'Robbery', emoji: '🚨', subtitle: 'Overpriced' },
];

const PORTION_OPTIONS: RatingOption[] = [
  { id: 'snack_attack', label: 'Snack', emoji: '🐁', subtitle: 'Too small' },
  { id: 'just_right', label: 'Just Right', emoji: '👌', subtitle: 'Perfect fill' },
  { id: 'food_coma', label: 'Food Coma', emoji: '🐷', subtitle: 'Massive portion' },
];

const TASTE_OPTIONS: RatingOption[] = [
  { id: 'meh', label: 'Meh', emoji: '😐', subtitle: 'Not great' },
  { id: 'solid', label: 'Solid', emoji: '👍', subtitle: 'Good standard' },
  { id: 'fire', label: 'Fire', emoji: '🔥', subtitle: 'Delicious' },
  { id: 'life_changing', label: 'Life-Changing', emoji: '🤯', subtitle: 'Best ever' },
];

interface RatingRowProps {
  label: string;
  options: RatingOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const RatingRow: React.FC<RatingRowProps> = ({ label, options, selectedId, onSelect }) => {
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = selectedId === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onSelect(option.id)}
              className={cn(
                "flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl border text-sm font-medium transition-all duration-200",
                "min-h-[56px] min-w-[80px] touch-manipulation", // Mobile touch target
                isSelected
                  ? "bg-[#cfef00] border-[#cfef00] text-black shadow-md scale-105"
                  : "bg-background border-border text-muted-foreground hover:border-[#cfef00]/50 hover:bg-[#cfef00]/10"
              )}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base">{option.emoji}</span>
                <span>{option.label}</span>
              </div>
              <span className={cn(
                "text-xs",
                isSelected ? "text-black/70" : "text-muted-foreground"
              )}>
                {option.subtitle}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const ReviewMatrix: React.FC<ReviewMatrixProps> = ({ value, onChange }) => {
  return (
    <div className="space-y-5 p-4 rounded-xl bg-muted/30 border border-border">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-foreground">Rate This Dish</h3>
        <p className="text-sm text-muted-foreground">Tap to share your honest take</p>
      </div>

      <RatingRow
        label="💰 Value"
        options={VALUE_OPTIONS}
        selectedId={value.ratingValue}
        onSelect={(id) => onChange('ratingValue', id)}
      />

      <RatingRow
        label="🍽️ Portion"
        options={PORTION_OPTIONS}
        selectedId={value.ratingPortion}
        onSelect={(id) => onChange('ratingPortion', id)}
      />

      <RatingRow
        label="👅 Taste"
        options={TASTE_OPTIONS}
        selectedId={value.ratingTaste}
        onSelect={(id) => onChange('ratingTaste', id)}
      />
    </div>
  );
};

export default ReviewMatrix;